import type { CfFlowResponse, CfFlowScreen, CfFlowStatus } from './api/cloudflareFlowApi';

/** Below this, the customer never left — they reloaded, or a second tab opened beside the first. */
export const NEVER_LEFT_GAP_MS = 5 * 60_000;

export const CF_STEP_NAMES = [
  'account_check',
  'token',
  'nameservers',
  'propagation',
  'deployment',
  'verification',
  'success',
  'reconnect',
  'error',
  'blocked',
  'abandoned',
  'recommendation',
] as const;

export type CfStepName = (typeof CF_STEP_NAMES)[number];

export const CF_NEXUS_FROM_STEPS = [...CF_STEP_NAMES, 'entry'] as const;

export type CfNexusFromStep = (typeof CF_NEXUS_FROM_STEPS)[number];

const FUNNEL: readonly CfStepName[] = [
  'account_check',
  'token',
  'nameservers',
  'propagation',
  'deployment',
  'verification',
  'success',
];

const SERVER_COMPLETED: readonly CfStepName[] = ['propagation', 'deployment', 'verification'];

const DEPLOYMENT_STATUSES: readonly CfFlowStatus[] = ['token_connected', 'preflight_running', 'deploying'];

const ENDED_STATUSES: readonly CfFlowStatus[] = ['abandoned', 'blocked', 'complete'];

export const isEndedFlow = (status: CfFlowStatus): boolean => ENDED_STATUSES.includes(status);

const progressStep = (status: CfFlowStatus): CfStepName | null => {
  if (DEPLOYMENT_STATUSES.includes(status)) return 'deployment';
  if (status === 'verifying') return 'verification';

  return null;
};

const STEP_OF_SCREEN: Readonly<Record<Exclude<CfFlowScreen, 'progress'>, CfStepName>> = {
  cf_account: 'account_check',
  token: 'token',
  reconnect: 'reconnect',
  nameservers: 'nameservers',
  propagation: 'propagation',
  error: 'error',
  blocked: 'blocked',
  abandoned: 'abandoned',
  success: 'success',
};

const failedStep = (status: CfFlowStatus): CfStepName => (status === 'verification_failed' ? 'verification' : 'error');

export const cfStepName = (screen: CfFlowScreen, status: CfFlowStatus): CfStepName | null => {
  if (screen === 'progress') return progressStep(status);
  if (screen === 'error') return failedStep(status);

  return STEP_OF_SCREEN[screen];
};

export const isForwardMove = (from: CfStepName, to: CfStepName): boolean => {
  const toRank = FUNNEL.indexOf(to);
  if (toRank === -1) return false;
  if (from === 'reconnect') return true;

  const fromRank = FUNNEL.indexOf(from);

  return fromRank !== -1 && toRank > fromRank;
};

/** The flow's createdAt; null for a screen shown before or outside a flow, and for a record stored before flows were told apart. */
export type FlowIdentity = string | null;

export type StepRecord = { step: CfStepName; at: number; completed: readonly CfStepName[]; flow: FlowIdentity };

const isStepName = (value: unknown): value is CfStepName => CF_STEP_NAMES.includes(value as CfStepName);

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const parseStepRecord = (raw: string | null): StepRecord | null => {
  const parsed = raw === null ? null : parseJson(raw);
  if (typeof parsed !== 'object' || parsed === null) return null;

  const { step, at, completed, flow } = parsed as Record<string, unknown>;
  if (!isStepName(step) || typeof at !== 'number' || !Number.isFinite(at)) return null;

  return {
    step,
    at,
    completed: Array.isArray(completed) ? completed.filter(isStepName) : [],
    flow: typeof flow === 'string' ? flow : null,
  };
};

export type StepVisit = {
  recorded: StepRecord | null;
  step: CfStepName;
  flow: FlowIdentity;
  subCase?: CfFlowResponse['subCase'];
};

export type StepEvent = { type: 'viewed' | 'completed'; step: CfStepName };

export type StepDecision = {
  events: readonly StepEvent[];
  record: StepRecord;
};

const completedEvent = (step: CfStepName): StepEvent => ({ type: 'completed', step });

const hasPropagationStep = (subCase: StepVisit['subCase']): boolean => subCase === 'not_on_cf';

const serverStepsBetween = (after: CfStepName | null, step: CfStepName, subCase: StepVisit['subCase']): CfStepName[] => {
  const stepRank = FUNNEL.indexOf(step);
  if (stepRank === -1) return [];

  const afterRank = after === null ? -1 : FUNNEL.indexOf(after);

  return FUNNEL.slice(afterRank + 1, stepRank).filter(
    (candidate) =>
      SERVER_COMPLETED.includes(candidate) && (candidate !== 'propagation' || hasPropagationStep(subCase)),
  );
};

const isFirstSightOrForwardMove = (recorded: StepRecord | null, step: CfStepName): boolean =>
  recorded === null || isForwardMove(recorded.step, step);

const stepsCompletedBeforeViewing = (
  recorded: StepRecord | null,
  step: CfStepName,
  subCase: StepVisit['subCase'],
): CfStepName[] => {
  if (!isFirstSightOrForwardMove(recorded, step)) return [];

  const alreadyCompleted = recorded?.completed ?? [];
  const leftBehind = recorded === null ? [] : [recorded.step];

  return [...leftBehind, ...serverStepsBetween(recorded?.step ?? null, step, subCase)].filter(
    (candidate) => !alreadyCompleted.includes(candidate),
  );
};

export const decideStepEvents = ({ recorded: anyFlowRecord, step, flow, subCase }: StepVisit, now: number): StepDecision => {
  const recorded = anyFlowRecord?.flow === flow ? anyFlowRecord : null;
  const alreadyCompleted = recorded?.completed ?? [];
  const viewed: StepEvent = { type: 'viewed', step };

  if (recorded?.step === step) {
    return {
      events: now - recorded.at >= NEVER_LEFT_GAP_MS ? [viewed] : [],
      record: { step, at: now, completed: alreadyCompleted, flow },
    };
  }

  const completedBeforeView = stepsCompletedBeforeViewing(recorded, step, subCase);
  const successCompleted = step === 'success' && !alreadyCompleted.includes(step) ? [step] : [];

  return {
    events: [...completedBeforeView.map(completedEvent), viewed, ...successCompleted.map(completedEvent)],
    record: { step, at: now, completed: [...alreadyCompleted, ...completedBeforeView, ...successCompleted], flow },
  };
};
